import { shallowEqual } from "react-redux";
import { GuxCopyToClipboard, GuxIcon } from "genesys-spark-components-react";
import "./AiScoringMCQOption.less";
import strings from "./AiScoringMCQOption.strings";
import { useStrings } from "features/i18n/useStrings";
import { ScoreTag, ScoreTagLevel } from "features/common/ScoreTag";
import { useAppSelector } from "app/hooks";
import {
    selectCanUserViewAiScoringExplanation,
    selectCanUserViewSensitiveData,
} from "features/auth/currentUserSlice";
import { MCQOptionRadioInput } from "../MCQOptionRadioInput";

// Feature slice commons
import { selectAnswerOption } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

export function AiAnswerExplanation({ explanation }: { explanation: string }) {
    const t = useStrings(strings);

    const canViewSensitiveData = useAppSelector(selectCanUserViewSensitiveData);

    const explanationText = (explanation ?? "").trim();
    const hasExplanation = explanationText.length > 0;

    /**
     * Visibility rule for this component :
     * - This component only renders for users with transcript view/recording view/segment view permission.
     * - If the user lacks permission to view sensitive data, hide the component entirely.
     */
    if (!canViewSensitiveData) return null;

    // if the user has all the right permissions to view the explanation, yet the explanation is unavailable, then isExplanationUnavailable would be true.
    const isExplanationUnavailable = !hasExplanation && canViewSensitiveData;

    return (
        <div
            className={`ai-answer-explanation ${
                isExplanationUnavailable ? "warning" : ""
            }`}
            data-testid="ai-answer-explanation-container"
        >
            <div className="title" data-testid="ai-answer-explanation-title">
                {t.labels.reason}
            </div>
            <GuxCopyToClipboard>
                <div
                    slot="content"
                    className="explanation"
                    data-testid="ai-answer-explanation"
                >
                    {isExplanationUnavailable ? (
                        <div
                            className="warning-content"
                            data-testid="ai-answer-explanation-warning"
                        >
                            <GuxIcon
                                iconName="fa/triangle-exclamation-solid"
                                decorative={true}
                                data-testid="ai-answer-warning-icon"
                            />
                            {t.labels.reasonUnavailable}
                        </div>
                    ) : (
                        explanation
                    )}
                </div>
            </GuxCopyToClipboard>
        </div>
    );
}

export function AiScoringMCQOption({
    answerOptionId,
    selected,
    disabled,
    questionLevel = "default",
    onSelected,
    hideScore = false,
    explanation,
}: {
    answerOptionId: string;
    selected: boolean;
    disabled: boolean;
    questionLevel?: ScoreTagLevel;
    onSelected: (s: string) => void;
    hideScore?: boolean;
    explanation: string;
}) {
    const { text, value } = useAppSelector((state) => {
        const { text, value } = selectAnswerOption(state, answerOptionId) ?? {
            text: "",
            value: "",
        };
        return { text, value };
    }, shallowEqual);
    const t = useStrings(strings);
    const canViewAiScoringExplanation = useAppSelector(
        selectCanUserViewAiScoringExplanation
    );

    return (
        <div className="AiScoringMCQOption" data-testid="ai-scoring-mcq-option">
            <div
                className={`MultipleChoiceQuestionOption ${
                    selected ? "selected" : ""
                }`}
            >
                <MCQOptionRadioInput
                    answerOptionId={answerOptionId}
                    selected={selected}
                    disabled={disabled}
                    onSelected={onSelected}
                    text={text}
                    type="ai-scoring"
                />
                <div className="ai-answer-details">
                    <div
                        className="ai-suggested"
                        data-testid="ai-suggested-tag"
                    >
                        <span>{t.labels.aiSuggested}</span>
                    </div>

                    {disabled && !hideScore && (
                        <div className="score">
                            <ScoreTag mode={questionLevel}>{value}</ScoreTag>
                        </div>
                    )}
                </div>
            </div>
            {canViewAiScoringExplanation && (
                <AiAnswerExplanation explanation={explanation} />
            )}
        </div>
    );
}
